import { z } from 'zod';
import {
  LIMITS,
  descriptionSchema,
  documentIdSchema,
  finiteNumber,
  nameSchema,
  positionSchema,
  timestampSchema,
} from './common';

/** バンク角の絶対値の上限 [rad]。2 回転（4π）まで。ひねりを重ねたコースを許す。 */
export const MAX_BANK = 4 * Math.PI;

/** 区間種別（この制御点から次の制御点までの区間）。 */
export const SEGMENT_TYPES = ['normal', 'lift', 'brake', 'station', 'launch', 'block'] as const;

/** 区間種別。 */
export type SegmentType = (typeof SEGMENT_TYPES)[number];

const controlPointBase = {
  /** コース内で一意の ID（例：`c1`） */
  id: z.string().min(1).max(64),
  /** 位置 [m] */
  position: positionSchema,
  /** バンク角 [rad]。正の向きは物理仕様 2.2 節（接線まわりの右ねじ回転） */
  bank: finiteNumber.min(-MAX_BANK).max(MAX_BANK).default(0),
};

/** リフトのパラメータ（物理仕様 4.3 節）。 */
export const liftParamsSchema = z.object({
  /** 巻き上げ速度 [m/s] */
  liftSpeed: finiteNumber.positive().max(30).default(3),
});

/** ブレーキのパラメータ（物理仕様 4.2 節）。 */
export const brakeParamsSchema = z.object({
  /** 最大減速度 [m/s²] */
  maxDeceleration: finiteNumber.positive().max(50).default(6),
  /** 目標速度 [m/s]。0 は停止ブレーキ */
  targetSpeed: finiteNumber.min(0).max(100).default(0),
});

/** 駅のパラメータ（物理仕様 4.5 節）。 */
export const stationParamsSchema = z.object({
  /** 発車時の送り出し速度 [m/s] */
  dispatchSpeed: finiteNumber.positive().max(30).default(1.5),
});

/** 発進加速のパラメータ（物理仕様 4.4 節。P3）。 */
export const launchParamsSchema = z.object({
  /** 目標速度 [m/s] */
  targetSpeed: finiteNumber.positive().max(200),
  /** 最大加速度 [m/s²] */
  maxAcceleration: finiteNumber.positive().max(100),
});

/**
 * 制御点。区間種別ごとにパラメータの形が決まる（`segment` で判別する）。
 * パラメータを省略すると既定値を補う。
 */
export const controlPointSchema = z.discriminatedUnion('segment', [
  z.object({ ...controlPointBase, segment: z.literal('normal') }),
  z.object({
    ...controlPointBase,
    segment: z.literal('lift'),
    segmentParams: liftParamsSchema.default({ liftSpeed: 3 }),
  }),
  z.object({
    ...controlPointBase,
    segment: z.literal('brake'),
    segmentParams: brakeParamsSchema.default({ maxDeceleration: 6, targetSpeed: 0 }),
  }),
  z.object({
    ...controlPointBase,
    segment: z.literal('station'),
    segmentParams: stationParamsSchema.default({ dispatchSpeed: 1.5 }),
  }),
  z.object({
    ...controlPointBase,
    segment: z.literal('launch'),
    segmentParams: launchParamsSchema,
  }),
  z.object({ ...controlPointBase, segment: z.literal('block') }),
]);

/** 制御点。 */
export type ControlPoint = z.infer<typeof controlPointSchema>;

/** 曲線方式（ADR-0006）。将来 `b-spline` などを追加する。 */
export const curveSchema = z.object({
  type: z.literal('catmull-rom'),
  /** Catmull-Rom のパラメータ（0.5 = centripetal） */
  alpha: finiteNumber.min(0).max(1).default(0.5),
});

/** コース。 */
export const courseSchema = z
  .object({
    id: documentIdSchema,
    name: nameSchema,
    description: descriptionSchema.default(''),
    tags: z.array(z.string().trim().min(1).max(30)).max(20).default([]),
    /** 周回コースか */
    closed: z.boolean(),
    curve: curveSchema.default({ type: 'catmull-rom', alpha: 0.5 }),
    controlPoints: z.array(controlPointSchema).min(2).max(LIMITS.maxControlPoints),
    defaultVehicleId: documentIdSchema.optional(),
    defaultEnvironmentId: documentIdSchema.optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine((course, context) => {
    if (course.closed && course.controlPoints.length < 3) {
      context.addIssue({
        code: 'custom',
        path: ['controlPoints'],
        message: '周回コースには制御点が 3 点以上必要です',
      });
    }
    const seen = new Set<string>();
    course.controlPoints.forEach((point, index) => {
      if (seen.has(point.id)) {
        context.addIssue({
          code: 'custom',
          path: ['controlPoints', index, 'id'],
          message: `制御点の ID が重複しています: ${point.id}`,
        });
      }
      seen.add(point.id);
    });
  });

/** コース。 */
export type Course = z.infer<typeof courseSchema>;
